import React, { useContext, useEffect, useState, useCallback } from 'react';

import { Checkbox, FormControlLabel } from '@mui/material';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import CardStatusNE from '../../../shared/components/CardStatusNE/CardStatusNE';
import DatePicker from '../../../shared/components/DatePicker/DatePicker';
import StoryUploader from '../../../shared/components/StoryUploader/StoryUploader';
import StyledButtonNE from '../../../shared/components/StyledButtonNE/StyledButtonNE';
import TextField from '../../../shared/components/TextField/TextField';
import ToastNE from '../../../shared/components/ToastNE/ToastNE';
import SecondaryHero from '../../../shared/components/v3/Heros/SecondaryHero/SecondaryHero';
import { STORIES_ROUTE } from '../../../shared/constants/auth';
import { PROGRAM_STORY_MODAL, PUBLISH_STORY_MODAL } from '../../../shared/constants/modal';
import {
  WELLBEING_INFO_TYPES,
  MEDICAL_CHECK_UP_STORY_IMAGE,
  SLEEP_PROBLEMS_STORY_IMAGE
} from '../../../shared/constants/reports';
import { STORIES_ERROR_CODES, STORIES_UPLOADER_VALIDATORS } from '../../../shared/constants/stories';
import {
  CANCEL_CREATE_STORY,
  CANCEL_POST_STORY,
  CLICK_ACTION,
  CREATE_STORY,
  POST_STORY
} from '../../../shared/constants/tagManager';
import {
  DEFAULT_TOAST_DATA,
  INVALID_FORMAT_STORY_ERROR_TOAST,
  MAX_SIZE_IMAGE_STORY_ERROR_TOAST,
  MAX_SIZE_VIDEO_STORY_ERROR_TOAST,
  PROGRAM_STORY_SUCCESS_TOAST,
  PUBLISH_STORY_ERROR_TOAST,
  PUBLISH_STORY_SUCCESS_TOAST,
  UPLOAD_STORY_ERROR_TOAST,
  UPLOAD_STORY_SERVICE_ERROR_TOAST
} from '../../../shared/constants/toast';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { ModalContext } from '../../../shared/context/modal/modalContext';
import { StoriesContext } from '../../../shared/context/stories/storiesContext';
import { useIsMounted } from '../../../shared/hooks/useIsMounted';
import { IToast } from '../../../shared/interfaces/toast';
import Stories from '../../../shared/services/api/communication/stories';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_STORIES_CREATE_NEW } from '../../../shared/translations/stories';
import { StoryType, StoryTypeEnum } from '../../../shared/types/stories';

import './StoriesCreateNew.scss';

const StoriesCreateNew = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [mediaFile, setMediaFile] = useState<File>();
  const [mediaUrl, setMediaUrl] = useState<string>();
  const [mediaType, setMediaType] = useState<StoryType>();
  const [uploadingProgress, setUploadingProgress] = useState<number>();
  const [permanent, setPermanent] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);

  const [toast, setToast] = useState<IToast>(DEFAULT_TOAST_DATA);
  const [isOpenToast, setIsOpenToast] = useState(false);

  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const location = useLocation();
  const [preloadedFromQuery, setPreloadedFromQuery] = useState(false);

  const { company } = useContext(CompanyContext);
  const { handleCloseModal, handleOpenModal } = useContext(ModalContext);
  const { storyDuplication, changeOpenToast, changeToastData } = useContext(StoriesContext);

  const today = (): Moment => moment();
  const in1Year = (): Moment => moment().add(1, 'year');
  const isToday = (date: Moment | null): boolean => date?.isSame(today(), 'day') ?? false;

  const handleOpenToast = useCallback((newToast: IToast) => {
    setToast(newToast);
    setIsOpenToast(true);
  }, []);

  const handleCloseToast = (_event: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsOpenToast(false);
  };

  const goToStoriesList = () => {
    navigate(STORIES_ROUTE);
  };

  const cancelCreateStory = () => {
    TagManagerService.pushEvent(CLICK_ACTION, CANCEL_CREATE_STORY);
    resetForm();
    goToStoriesList();
  };

  const handleOnChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const checkTitleIsInvalid = (title: string): boolean => {
    return title === null || title === undefined || title === '';
  };

  const checkDateIsInvalid = (date: Moment | null): boolean => {
    return date === null || date === undefined;
  };

  const checkEndDateIsInvalid = (date: Moment | null): boolean => {
    if (permanent) return false;
    return checkDateIsInvalid(date);
  };

  const checkFormIsValid = (): boolean => {
    return (
      !checkTitleIsInvalid(title) &&
      !checkDateIsInvalid(startDate) &&
      !checkEndDateIsInvalid(endDate) &&
      mediaUrl !== undefined &&
      mediaType !== undefined
    );
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(null);
    e.target.checked ? setPermanent(true) : setPermanent(false);
  };

  const saveFile = (file: File) => {
    saveMediaType(file);
    setMediaFile(file);
  };

  const saveMediaType = (file: File) => {
    const extension = file.type.split('/')[0];
    if (extension === 'video') setMediaType(StoryTypeEnum.video);
    else if (extension === 'image') setMediaType(StoryTypeEnum.image);
  };

  const deleteFile = () => {
    setMediaFile(undefined);
    setMediaUrl(undefined);
    setMediaType(undefined);
    setUploadingProgress(undefined);
  };

  const resetForm = () => {
    setTitle('');
    setPermanent(false);
    setStartDate(null);
    setEndDate(null);
    deleteFile();
  };

  interface UploadProgressEvent {
    loaded: number;
    total: number;
  }
  const onUploadProgress = useCallback((event: UploadProgressEvent): void => {
    const percentCompleted = Math.round((event.loaded * 100) / event.total);
    setUploadingProgress(percentCompleted);
  }, []);

  const showUploadError = useCallback(
    (code?: number) => {
      if (code === undefined) {
        handleOpenToast(UPLOAD_STORY_ERROR_TOAST);
        return;
      }

      switch (code) {
        case STORIES_ERROR_CODES.UPLOAD_STORY_SERVICE_ERROR:
          handleOpenToast(UPLOAD_STORY_SERVICE_ERROR_TOAST);
          break;
        case STORIES_ERROR_CODES.INVALID_FORMAT_STORY_ERROR:
          handleOpenToast(INVALID_FORMAT_STORY_ERROR_TOAST);
          break;
        case STORIES_ERROR_CODES.MAX_SIZE_IMAGE_STORY_ERROR:
          handleOpenToast(MAX_SIZE_IMAGE_STORY_ERROR_TOAST);
          break;
        case STORIES_ERROR_CODES.MAX_SIZE_VIDEO_STORY_ERROR:
          handleOpenToast(MAX_SIZE_VIDEO_STORY_ERROR_TOAST);
          break;
        default:
          handleOpenToast(UPLOAD_STORY_ERROR_TOAST);
          break;
      }
    },
    [handleOpenToast]
  );

  const uploadMediaFile = useCallback(async () => {
    if (!mediaFile || !mediaType) return;

    try {
      setUploadingProgress(0);
      const response = await Stories.uploadStoryFile(company.id, mediaFile, mediaType, onUploadProgress);
      setMediaUrl(response.url);
    } catch (error: unknown) {
      deleteFile();
      let code: number | undefined;
      if (typeof error === 'object' && error !== null) {
        const resp = (error as { response?: { data?: { code?: number }; status?: number } }).response;
        code = resp?.data?.code ?? resp?.status;
      }
      showUploadError(code);
    } finally {
      setUploadingProgress(undefined);
    }
  }, [company.id, mediaFile, mediaType, onUploadProgress, showUploadError]);

  const closeModal = () => {
    TagManagerService.pushEvent(CLICK_ACTION, CANCEL_POST_STORY);
    handleCloseModal();
  };

  const openConfirmationModal = () => {
    TagManagerService.pushEvent(CLICK_ACTION, CREATE_STORY);

    if (isToday(startDate)) {
      if (PUBLISH_STORY_MODAL.primaryButton) {
        PUBLISH_STORY_MODAL.primaryButton.action = publishStory;
      }
      if (PUBLISH_STORY_MODAL.secondaryButton) {
        PUBLISH_STORY_MODAL.secondaryButton.action = closeModal;
      }
      handleOpenModal(PUBLISH_STORY_MODAL);
    } else {
      if (PROGRAM_STORY_MODAL.primaryButton) {
        PROGRAM_STORY_MODAL.primaryButton.action = publishStory;
      }
      if (PROGRAM_STORY_MODAL.secondaryButton) {
        PROGRAM_STORY_MODAL.secondaryButton.action = closeModal;
      }
      handleOpenModal(PROGRAM_STORY_MODAL);
    }
  };

  const publishStory = async () => {
    if (!checkFormIsValid() || !mediaType || !mediaUrl) return;

    try {
      handleCloseModal();
      setLoading(true);
      const startDateStr = startDate?.format() ?? '';
      const endDateStr = permanent || endDate === null ? null : endDate?.format();
      const response = await Stories.createStory(company.id, mediaType, mediaUrl, title, startDateStr, endDateStr);
      TagManagerService.pushEvent(CLICK_ACTION, POST_STORY, { id: response.storyId, type: mediaType });
      changeToastData(isToday(startDate) ? PUBLISH_STORY_SUCCESS_TOAST : PROGRAM_STORY_SUCCESS_TOAST);
      resetForm();
    } catch {
      changeToastData(PUBLISH_STORY_ERROR_TOAST);
    } finally {
      setLoading(false);
      changeOpenToast(true);
      goToStoriesList();
    }
  };

  useEffect(() => {
    if (storyDuplication) {
      setMediaFile(undefined);
      setUploadingProgress(undefined);
      setTitle(storyDuplication.title);
      setPermanent(storyDuplication.status === 'permanent' || storyDuplication.endDate === undefined);

      if (moment(storyDuplication.startDate, 'DD/MM/YYYY').isSameOrAfter(today())) {
        setStartDate(moment(storyDuplication.startDate, 'DD/MM/YYYY'));
      } else {
        setStartDate(today());
      }

      if (storyDuplication.endDate && moment(storyDuplication.endDate, 'DD/MM/YYYY').isSameOrAfter(today())) {
        setEndDate(moment(storyDuplication.endDate, 'DD/MM/YYYY'));
      } else {
        setEndDate(null);
      }

      setMediaType(storyDuplication.mediaType);
      setMediaUrl(storyDuplication.contentUrl);
    }
  }, [storyDuplication]);

  useEffect(() => {
    if (isMounted) {
      uploadMediaFile();
    }
  }, [isMounted, uploadMediaFile]);

  useEffect(() => {
    if (preloadedFromQuery || mediaUrl || mediaFile) return;
    const params = new URLSearchParams(location.search);
    const typeParam = params.get('type');
    if (!typeParam) return;
    if (typeParam !== WELLBEING_INFO_TYPES.medicalCheckUp && typeParam !== WELLBEING_INFO_TYPES.sleepProblems) return;
    const mapping: Record<string, string> = {
      [WELLBEING_INFO_TYPES.medicalCheckUp]: MEDICAL_CHECK_UP_STORY_IMAGE,
      [WELLBEING_INFO_TYPES.sleepProblems]: SLEEP_PROBLEMS_STORY_IMAGE
    };
    const mappedUrl = mapping[typeParam];
    if (!mappedUrl) return;
    setMediaUrl(mappedUrl);
    setMediaType(StoryTypeEnum.image);
    setPreloadedFromQuery(true);
  }, [location.search, mediaUrl, mediaFile, preloadedFromQuery]);

  return (
    <div className="stories-create-container" data-experience="new">
      <SecondaryHero
        title={t(TRANSLATION_STORIES_CREATE_NEW.header.pagename)}
        description={t(TRANSLATION_STORIES_CREATE_NEW.header.heroDescription)}
        color="hero-stories"
        backAction={goToStoriesList}
      />
      <div className="stories-create-content">
        <div className="flex flex-row gap-10">
          <div className="flex flex-column gap-4">
            <label htmlFor="title" className="ne-font-body-md font-bold">
              {t(TRANSLATION_STORIES_CREATE_NEW.form.labelFile)}
            </label>
            <StoryUploader
              validator={STORIES_UPLOADER_VALIDATORS}
              deleteFile={deleteFile}
              saveFile={saveFile}
              returnError={showUploadError}
              previewUrl={mediaUrl}
              previewMediaType={mediaType}
              uploading={uploadingProgress}
              title={title}
            />
          </div>
          <div className="grow flex flex-column gap-9">
            <div className="flex flex-column gap-4">
              <label htmlFor="title" className="ne-font-body-md font-bold">
                {t(TRANSLATION_STORIES_CREATE_NEW.form.labelTitle)}
              </label>
              <TextField
                value={title ?? ''}
                onChange={handleOnChangeTitle}
                type="text"
                label={t(TRANSLATION_STORIES_CREATE_NEW.form.placeholderTitle)}
                id="storyTitle"
              />
            </div>
            <div className="flex flex-column gap-4">
              <label htmlFor="title" className="ne-font-body-md font-bold">
                {t(TRANSLATION_STORIES_CREATE_NEW.form.labelDates)}
              </label>
              <FormControlLabel
                control={<Checkbox color="primary" onChange={handleCheckbox} checked={permanent} />}
                label={t<string>(TRANSLATION_STORIES_CREATE_NEW.form.checkbox)}
              />
              <CardStatusNE type="informative" message={t(TRANSLATION_STORIES_CREATE_NEW.form.cardStatus)} />
              <div className="flex flex-row justify-between gap-9">
                <div className="grow">
                  <DatePicker
                    label={t(TRANSLATION_STORIES_CREATE_NEW.form.placeholderStartDate)}
                    value={startDate ?? null}
                    minDate={today()}
                    maxDate={endDate ?? in1Year()}
                    onChange={(date) => setStartDate(date)}
                    className="w-full"
                    id="storyStartDate"
                  />
                </div>
                <div className="grow">
                  <DatePicker
                    label={t(TRANSLATION_STORIES_CREATE_NEW.form.placeholderEndDate)}
                    value={endDate ?? null}
                    minDate={startDate ?? today()}
                    maxDate={permanent ? undefined : in1Year()}
                    onChange={(date) => setEndDate(date)}
                    className="w-full"
                    disabled={permanent}
                    id="storyEndDate"
                  />
                </div>
              </div>
            </div>
            <CardStatusNE type="alert" message={t(TRANSLATION_STORIES_CREATE_NEW.form.cardStatusWarning)} />
            <div className="flex flex-row justify-end gap-9">
              <div>
                <StyledButtonNE buttonType="secondary" buttonSize="md" action={cancelCreateStory}>
                  {t(TRANSLATION_STORIES_CREATE_NEW.form.cancelButton)}
                </StyledButtonNE>
              </div>
              <div>
                <StyledButtonNE
                  buttonType="primary"
                  buttonSize="md"
                  action={openConfirmationModal}
                  disabled={!checkFormIsValid()}
                  loading={loading}
                >
                  {t(TRANSLATION_STORIES_CREATE_NEW.form.publishButton)}
                </StyledButtonNE>
              </div>
            </div>
          </div>
        </div>
        <ToastNE
          title={t(toast.title)}
          description={t(toast.description)}
          icon={toast.icon}
          open={isOpenToast}
          button={true}
          buttonText={t(toast.buttonText)}
          buttonAction={handleCloseToast}
          handleClose={handleCloseToast}
        />
      </div>
    </div>
  );
};

export default StoriesCreateNew;
